import React, { useRef, useState } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Gallery uploader for product / service photos.
 *
 * Renders a row of slots (max = `max`, default 6). Each slot is either an
 * existing image (with an X button to remove) or a "+" button that opens the
 * device's file picker (camera or gallery). Uploads go to
 * `POST /api/uploads/image?purpose=product` and the returned URL is appended
 * to the value array.
 *
 * Props:
 *  - value: string[]         current gallery URLs
 *  - onChange: (arr) => void
 *  - max: number             default 6
 *  - label, hint
 */
const compress = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxSide = 1200;
        let w = img.width;
        let h = img.height;
        const ratio = Math.min(maxSide / w, maxSide / h, 1);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        let quality = 0.85;
        const toBlob = (q) =>
          new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
        (async () => {
          let blob = await toBlob(quality);
          while (blob && blob.size > 400 * 1024 && quality > 0.4) {
            quality -= 0.1;
            blob = await toBlob(quality);
          }
          if (!blob) return reject(new Error("compress failed"));
          resolve(blob);
        })();
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const GalleryUpload = ({
  value = [],
  onChange,
  max = 6,
  label,
  hint,
  testid = "gallery-upload",
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const remaining = Math.max(0, max - value.length);

  const handleFiles = async (files) => {
    setError("");
    if (!files || !files.length) return;
    const list = Array.from(files).slice(0, remaining);
    setUploading(true);
    const added = [];
    for (const file of list) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > 5 * 1024 * 1024) {
        setError("Alguna imagen supera 5 MB y fue ignorada.");
        continue;
      }
      try {
        const blob = await compress(file);
        const fd = new FormData();
        fd.append("file", blob, "gallery.jpg");
        const { data } = await api.post(
          "/uploads/image?purpose=product",
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        added.push(data.url);
      } catch (err) {
        setError(err?.response?.data?.detail || "No se pudo subir una imagen.");
      }
    }
    if (added.length) onChange([...value, ...added]);
    setUploading(false);
  };

  const removeAt = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const slots = Array.from({ length: max });

  return (
    <div data-testid={`${testid}-wrapper`}>
      {label && (
        <label className="text-xs font-bold uppercase tracking-wider text-teal-soft block mb-1">
          {label} <span className="text-gray-400 font-normal">({value.length}/{max})</span>
        </label>
      )}
      {hint && <p className="text-xs text-teal-soft mb-2">{hint}</p>}

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {slots.map((_, i) => {
          const url = value[i];
          if (url) {
            return (
              <div
                key={url}
                className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-cream group"
                data-testid={`${testid}-item-${i}`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/95 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center shadow"
                  aria-label="Quitar foto"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <X size={13} />
                </button>
              </div>
            );
          }
          if (i === value.length && remaining > 0) {
            return (
              <button
                key="slot-add"
                type="button"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-orange bg-cream/50 flex flex-col items-center justify-center text-teal-soft hover:text-orange transition-colors disabled:opacity-60"
                data-testid={`${testid}-add`}
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} />}
                <span className="text-[10px] font-semibold mt-0.5">
                  {uploading ? "Subiendo…" : "Agregar"}
                </span>
              </button>
            );
          }
          return (
            <div
              key={`slot-empty-${i}`}
              className="aspect-square rounded-xl border border-dashed border-gray-200 bg-cream/40"
              aria-hidden
            />
          );
        })}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
        data-testid={testid}
      />

      {error && (
        <p className="text-xs text-red-600 mt-2" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default GalleryUpload;
